import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import { FC, useContext, useEffect, useRef, useState } from 'react'
import { pdfjs } from 'react-pdf'
import { Renderer } from './core/Renderer'
import { observer } from 'mobx-react'
import { StoreContext } from '@store/Root.store'
import './PdfViewer.css'
import { safariMemoryManager } from './helpers/SafariMemoryManager'
import { useResizeObserver } from './hooks/useResizeObserver'
import { PageIndicator } from './addons/PageIndicator'
import { ZoomControls } from './addons/ZoomControls'
import { ScrollDocumentButton } from './addons/ScrollDocumentButton'
import { actionReport } from '@/methods/actionReport'
import { ErrorBoundary } from 'react-error-boundary'
import { devLog } from '@/methods/devLog'
import { PdfErrorContainer } from './core/PdfErrorContainer'
import { getDocumentUrl } from '@/methods/getDocumentUrl'
import type { EditableFieldsConfig } from '@/components/EditableFields/EditableFieldsOverlay'

import workerSrc from 'pdfjs-dist/build/pdf.worker.min.mjs?worker&url'
pdfjs.GlobalWorkerOptions.workerSrc = workerSrc

interface PdfViewerProps {
  file: string
  fileId?: string
  fileUrl: string | undefined
  service?: string
  onDocumentLastPage?: () => void
  showZoomControls?: boolean
  showScrollToBottomButton?: boolean
  initialScale?: number
  documentError: string | null
  setDocumentError: (error: string | null) => void
  onDocumentLoadSuccess?: () => void
  setAllowSigning?: (allow: boolean) => void
  viewerHeight?: number
  viewerWidth?: number
  editableFields?: EditableFieldsConfig
}
export const PdfViewer: FC<PdfViewerProps> = observer(
  ({
    file,
    fileId,
    fileUrl,
    service,
    onDocumentLastPage,
    showZoomControls,
    showScrollToBottomButton,
    initialScale,
    documentError,
    setDocumentError,
    onDocumentLoadSuccess,
    setAllowSigning,
    viewerHeight,
    viewerWidth,
    editableFields,
  }) => {
    const store = useContext(StoreContext)
    const { totalPages, currentPageIndex, shouldShowPageIndicator, setShouldShowPageIndicator } =
      store.PdfViewerState
    const { pdfViewer: transViewer } = store.TranslationsState.translations

    const [didAllowSigningSet, setDidAllowSigningSet] = useState(false)
    const [didStateWipe, setDidStateWipe] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null)
    const viewerDimensions = useResizeObserver(containerRef, 200)

    useEffect(() => {
      store.PdfViewerState.resetViewerState()
      setDidStateWipe(true)

      // Cleanup on app unmount
      return () => {
        safariMemoryManager.destroy()
      }
    }, [])

    useEffect(() => {
      if (totalPages === 1) {
        onDocumentLastPage && onDocumentLastPage()
      }
    }, [currentPageIndex, totalPages, fileId])

    const [viewerKey, setViewerKey] = useState(0)
    useEffect(() => {
      setViewerKey((prevKey) => prevKey + 1)
    }, [file, fileId])

    useEffect(() => {
      if (initialScale !== undefined) {
        store.PdfViewerState.setPageScale(initialScale)
      }
    }, [initialScale])

    useEffect(() => {
      if (!didAllowSigningSet) {
        // Force user to download document if worker is not working
        setAllowSigning && setAllowSigning(false)
        setDidAllowSigningSet(true)
      }
    }, [didAllowSigningSet])

    if (!didStateWipe) {
      return <></>
    }

    return (
      <ErrorBoundary
        fallbackRender={() => (
          <PdfErrorContainer
            height={viewerHeight || viewerDimensions.height}
            width={viewerWidth || viewerDimensions.width}
            failedToLoadPdf={transViewer?.cantDisplayTheDocument || ''}
            setAllowSigning={setAllowSigning}
            documentUrl={fileUrl || getDocumentUrl(fileId, service)}
          />
        )}
        onError={(error, errorInfo) => {
          devLog('Error caught by boundary in pdf viewer:', error, errorInfo)

          actionReport({
            type: 'event.onboarding-web.process.REACT_ERROR_BOUNDARY_CATCH',
            payload: {
              error: {
                message: error.message,
                stack: error.stack,
                name: error.name,
              },
              componentStack: errorInfo.componentStack,
              userAgent: navigator.userAgent,
            },
          })
        }}
      >
        <div
          ref={containerRef}
          key={`pdf-viewer-${viewerKey}`}
          className="pdf-viewer__container pdf-viewer-container"
        >
          <Renderer
            file={file}
            height={viewerHeight || viewerDimensions.height}
            width={viewerWidth || viewerDimensions.width}
            onDocumentLastPage={onDocumentLastPage}
            documentError={documentError}
            setDocumentError={setDocumentError}
            onDocumentLoadSuccess={onDocumentLoadSuccess}
            setAllowSigning={setAllowSigning}
            documentUrl={fileUrl || getDocumentUrl(fileId, service)}
            editableFields={editableFields}
          />

          <PageIndicator
            currentPage={currentPageIndex}
            totalPages={totalPages}
            shouldShow={shouldShowPageIndicator}
            setShouldShow={setShouldShowPageIndicator}
          />

          {showZoomControls && <ZoomControls />}

          <ScrollDocumentButton
            onClick={onDocumentLastPage}
            bottomOffset={showZoomControls ? 60 : 0}
            showScrollToBottomButton={showScrollToBottomButton}
          />
        </div>
      </ErrorBoundary>
    )
  }
)
